import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  InputAdornment,
  CircularProgress,
  Fade,
  Divider,
  IconButton,
  Popper,
  ClickAwayListener,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Inventory as ProductIcon,
  Category as CategoryIcon,
  SubdirectoryArrowRight as SubcategoryIcon,
  MiscellaneousServices as ServiceIcon,
  TrendingUp as TrendingIcon,
  History as HistoryIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { postRecord } from "services/services";

const SearchComponent = ({
  placeholder = "Search for products, categories, services...",
  variant = "outlined",
  size = "medium",
  fullWidth = true,
  sx = {},
  onSearchResult = null,
  showRecentSearches = true,
  maxResults = 8,
}) => {
  const [searchText, setSearchText] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const searchRef = useRef(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  // Load recent searches from localStorage
  useEffect(() => {
    if (showRecentSearches) {
      const saved = localStorage.getItem("recentSearches");
      if (saved) {
        try {
          setRecentSearches(JSON.parse(saved));
        } catch (e) {
          console.error("Failed to parse recent searches:", e);
        }
      }
    }
  }, [showRecentSearches]);

  // Save recent searches to localStorage
  const saveRecentSearch = useCallback(
    (searchTerm) => {
      if (!showRecentSearches || !searchTerm.trim()) return;

      const updated = [
        searchTerm,
        ...recentSearches.filter((item) => item !== searchTerm),
      ].slice(0, 5); // Keep only 5 recent searches

      setRecentSearches(updated);
      localStorage.setItem("recentSearches", JSON.stringify(updated));
    },
    [recentSearches, showRecentSearches]
  );

  // Clear recent searches
  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem("recentSearches");
  };

  // Debounced search function
  const performSearch = useCallback(
    async (query) => {
      if (query.length < 3) {
        setResults([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await postRecord("search/", {
          search_text: query,
        });

        console.log("Search API response:", response);

        if (response.status === 200 && response.data?.success) {
          const searchResults = response.data.data?.results || [];

          console.log("Parsed search results:", {
            totalResults: response.data.data?.total_results,
            searchText: response.data.data?.search_text,
            results: searchResults.map((r) => ({
              type: r.type,
              id: r.id,
              name: r.name,
              categoryName: r.category_name,
              subcategoryName: r.subcategory_name,
              defaultCombinationId: r.default_combination_id,
              defaultCombinationText: r.default_combination_text,
            })),
          });

          setResults(searchResults.slice(0, maxResults));
        } else {
          console.warn("Search API returned unsuccessful response:", response);
          setResults([]);
        }
      } catch (error) {
        console.error("Search error:", error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [maxResults]
  );

  // Handle input change with debouncing
  const handleInputChange = (event) => {
    const value = event.target.value;
    setSearchText(value);
    setSelectedIndex(-1);

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Set new debounce
    debounceRef.current = setTimeout(() => {
      performSearch(value);
    }, 300);

    // Show dropdown when typing
    if (value.length > 0 || (showRecentSearches && recentSearches.length > 0)) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  // Get icon for result type
  const getResultIcon = (type) => {
    switch (type) {
      case "product":
        return <ProductIcon fontSize="small" color="primary" />;
      case "category":
        return <CategoryIcon fontSize="small" color="secondary" />;
      case "subcategory":
        return <SubcategoryIcon fontSize="small" color="info" />;
      case "service":
        return <ServiceIcon fontSize="small" color="success" />;
      default:
        return <SearchIcon fontSize="small" />;
    }
  };

  // Get result type label
  const getResultTypeLabel = (type) => {
    switch (type) {
      case "product":
        return "Product";
      case "category":
        return "Category";
      case "subcategory":
        return "Subcategory";
      case "service":
        return "Service";
      default:
        return type;
    }
  };

  // Handle result click
  const handleResultClick = (result) => {
    saveRecentSearch(result.name);

    if (onSearchResult) {
      onSearchResult(result);
    }

    // Helper function to create URL-safe strings
    const createUrlSafeString = (str) => {
      return str
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, "") // Remove special characters except spaces and hyphens
        .replace(/\s+/g, "-") // Replace spaces with hyphens
        .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
        .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
    };

    // Helper function for product names - only lowercase, no space replacement
    const createProductUrlString = (str) => {
      return str.toLowerCase().trim();
    };

    // Navigate based on type
    try {
      switch (result.type) {
        case "product":
          // For products, navigate with product name and default combination
          const productName = createProductUrlString(result.name);

          console.log("Navigating to product:", {
            originalName: result.name,
            urlSafeName: productName,
            defaultCombinationId: result.default_combination_id,
            defaultCombinationText: result.default_combination_text,
            productId: result.id,
          });

          // Use default_combination_id with default_combination_text as combination text
          if (
            result.default_combination_id &&
            result.default_combination_text
          ) {
            navigate(
              encodeURI(
                `/ProductView/${productName}/${result.default_combination_text}/${result.default_combination_id}`
              )
            );
          } else {
            // Fallback: Navigate with just product name
            navigate(encodeURI(`/ProductView/${productName}`));
          }
          break;

        case "category":
          const categoryName = createUrlSafeString(result.name);
          navigate(encodeURI(`/ProductList/${categoryName}`));
          break;

        case "subcategory":
          // For subcategories, use both category_name and subcategory name
          const categoryForSubcat = createUrlSafeString(result.category_name);
          const subcategoryName = createUrlSafeString(result.name);

          console.log("Navigating to subcategory:", {
            category: result.category_name,
            subcategory: result.name,
            urlSafeCategory: categoryForSubcat,
            urlSafeSubcategory: subcategoryName,
          });

          // Navigate to ProductList with category and subcategory
          navigate(
            encodeURI(`/ProductList/${categoryForSubcat}/${subcategoryName}`)
          );
          break;

        case "service":
          const serviceName = createUrlSafeString(result.name);
          navigate(encodeURI(`/ServiceView/${serviceName}`));
          break;

        default:
          console.warn("Unknown result type:", result.type);
          // Fallback: try to navigate to a general search results page
          navigate(`/search?q=${encodeURIComponent(result.name)}`);
      }
    } catch (error) {
      console.error("Navigation error:", error);
      // Fallback: try to navigate to a general search results page
      navigate(`/search?q=${encodeURIComponent(result.name)}`);
    }

    // Close dropdown and clear search
    setOpen(false);
    setSearchText("");
    setResults([]);
  };

  // Handle recent search click
  const handleRecentSearchClick = (searchTerm) => {
    setSearchText(searchTerm);
    performSearch(searchTerm);
    setOpen(true);
  };

  // Handle keyboard navigation
  const handleKeyDown = (event) => {
    const totalItems =
      results.length +
      (showRecentSearches && searchText.length === 0
        ? recentSearches.length
        : 0);

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % totalItems);
        break;
      case "ArrowUp":
        event.preventDefault();
        setSelectedIndex((prev) => (prev <= 0 ? totalItems - 1 : prev - 1));
        break;
      case "Enter":
        event.preventDefault();
        if (selectedIndex >= 0) {
          if (searchText.length === 0 && showRecentSearches) {
            // Recent search selected
            handleRecentSearchClick(recentSearches[selectedIndex]);
          } else if (results[selectedIndex]) {
            // Search result selected
            handleResultClick(results[selectedIndex]);
          }
        } else if (searchText.length >= 3) {
          // Perform search on enter
          saveRecentSearch(searchText);
          setOpen(false);
        }
        break;
      case "Escape":
        setOpen(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Clear search
  const handleClear = () => {
    setSearchText("");
    setResults([]);
    setOpen(false);
    setSelectedIndex(-1);
    if (searchRef.current) {
      searchRef.current.focus();
    }
  };

  // Handle click away
  const handleClickAway = () => {
    setOpen(false);
    setSelectedIndex(-1);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: "relative", width: "100%", ...sx }}>
        <TextField
          ref={searchRef}
          value={searchText}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (
              searchText.length > 0 ||
              (showRecentSearches && recentSearches.length > 0)
            ) {
              setOpen(true);
            }
          }}
          placeholder={placeholder}
          variant={variant}
          size={size}
          fullWidth={fullWidth}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {loading && <CircularProgress size={20} />}
                {!loading && searchText && (
                  <IconButton size="small" onClick={handleClear}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-root": {
              borderRadius: "25px",
              backgroundColor: "transparent",
              transition: "all 0.3s ease",
              "& fieldset": {
                borderColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.3)"
                    : "rgba(0, 0, 0, 0.2)",
              },
              "&:hover": {
                backgroundColor: "transparent",
                "& fieldset": {
                  borderColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.5)"
                      : "rgba(0, 0, 0, 0.3)",
                },
              },
              "&.Mui-focused": {
                backgroundColor: "transparent",
                "& fieldset": {
                  borderColor: (theme) => theme.palette.primary.main,
                  borderWidth: "2px",
                },
              },
            },
            "& .MuiInputBase-input": {
              padding: { xs: "14px 16px", sm: "12px 14px" },
              fontSize: { xs: "16px", sm: "14px" }, // Prevent zoom on iOS
              color: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.9)"
                  : "rgba(0, 0, 0, 0.9)",
            },
            "& .MuiInputAdornment-root": {
              color: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.7)"
                  : "rgba(0, 0, 0, 0.6)",
            },
          }}
        />

        <Popper
          open={open}
          anchorEl={searchRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300, width: searchRef.current?.offsetWidth }}
          transition
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={200}>
              <Paper
                elevation={8}
                sx={{
                  mt: 1,
                  maxHeight: "400px",
                  overflowY: "auto",
                  overflowX: "hidden",
                  borderRadius: "12px",
                  border: (theme) =>
                    theme.palette.mode === "dark"
                      ? "1px solid rgba(255, 255, 255, 0.1)"
                      : "1px solid rgba(0, 0, 0, 0.08)",
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? theme.palette.background.paper
                      : theme.palette.background.paper,
                  boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                      ? "0 8px 32px rgba(0, 0, 0, 0.8)"
                      : "0 8px 32px rgba(0, 0, 0, 0.12)",
                }}
              >
                {/* Recent Searches */}
                {showRecentSearches &&
                  searchText.length === 0 &&
                  recentSearches.length > 0 && (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          p: 2,
                          pb: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <HistoryIcon fontSize="small" />
                          Recent Searches
                        </Typography>
                        <IconButton size="small" onClick={clearRecentSearches}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <List dense sx={{ py: 0 }}>
                        {recentSearches.map((search, index) => (
                          <ListItem
                            key={index}
                            button
                            selected={selectedIndex === index}
                            onClick={() => handleRecentSearchClick(search)}
                            sx={{
                              borderRadius: "8px",
                              mx: 1,
                              mb: 0.5,
                              width: "calc(100% - 16px)",
                              "&.Mui-selected": {
                                backgroundColor: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(255, 255, 255, 0.08)"
                                    : "rgba(25, 118, 210, 0.08)",
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <HistoryIcon fontSize="small" color="action" />
                            </ListItemIcon>
                            <ListItemText
                              primary={search}
                              primaryTypographyProps={{
                                fontSize: "0.9rem",
                                noWrap: true,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                      {results.length > 0 && <Divider sx={{ my: 1 }} />}
                    </>
                  )}

                {/* Search Results */}
                {results.length > 0 && (
                  <>
                    <Box sx={{ p: 2, pb: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <TrendingIcon fontSize="small" />
                        Search Results ({results.length})
                      </Typography>
                    </Box>
                    <List dense sx={{ py: 0 }}>
                      {results.map((result, index) => {
                        const adjustedIndex =
                          showRecentSearches && searchText.length === 0
                            ? index + recentSearches.length
                            : index;

                        return (
                          <ListItem
                            key={`${result.type}-${result.id}`}
                            button
                            selected={selectedIndex === adjustedIndex}
                            onClick={() => handleResultClick(result)}
                            sx={{
                              borderRadius: "8px",
                              mx: 1,
                              mb: 0.5,
                              width: "calc(100% - 16px)",
                              // Special highlighting for products
                              border:
                                result.type === "product"
                                  ? "1px solid"
                                  : "1px solid transparent",
                              borderColor:
                                result.type === "product"
                                  ? (theme) =>
                                      theme.palette.mode === "dark"
                                        ? "rgba(76, 175, 80, 0.3)"
                                        : "rgba(76, 175, 80, 0.2)"
                                  : "transparent",
                              "&.Mui-selected": {
                                backgroundColor: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(255, 255, 255, 0.08)"
                                    : "rgba(25, 118, 210, 0.08)",
                              },
                              "&:hover": {
                                backgroundColor: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(255, 255, 255, 0.05)"
                                    : "rgba(25, 118, 210, 0.05)",
                                borderColor:
                                  result.type === "product"
                                    ? (theme) =>
                                        theme.palette.mode === "dark"
                                          ? "rgba(76, 175, 80, 0.5)"
                                          : "rgba(76, 175, 80, 0.4)"
                                    : "transparent",
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              {getResultIcon(result.type)}
                            </ListItemIcon>
                            <ListItemText
                              primary={result.name}
                              primaryTypographyProps={{
                                fontSize: "0.9rem",
                                fontWeight: 500,
                                noWrap: true,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              secondary={
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mt: 0.5,
                                  }}
                                >
                                  <Chip
                                    label={getResultTypeLabel(result.type)}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      height: 20,
                                      fontSize: "0.7rem",
                                    }}
                                  />
                                  {/* Show category and subcategory info for products */}
                                  {result.type === "product" && (
                                    <>
                                      {result.category_name && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{
                                            fontSize: "0.7rem",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            maxWidth: "120px",
                                          }}
                                        >
                                          {result.subcategory_name
                                            ? `${result.category_name} › ${result.subcategory_name}`
                                            : result.category_name}
                                        </Typography>
                                      )}
                                    </>
                                  )}
                                  {/* Show category info for subcategories */}
                                  {result.type === "subcategory" &&
                                    result.category_name && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                          fontSize: "0.7rem",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          maxWidth: "120px",
                                        }}
                                      >
                                        in {result.category_name}
                                      </Typography>
                                    )}
                                  {/* Legacy support for price and category fields */}
                                  {result.type === "product" &&
                                    result.price && (
                                      <Chip
                                        label={`₹${result.price}`}
                                        size="small"
                                        color="primary"
                                        sx={{
                                          height: 20,
                                          fontSize: "0.7rem",
                                        }}
                                      />
                                    )}
                                  {result.type === "product" &&
                                    result.category &&
                                    !result.category_name && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        in {result.category}
                                      </Typography>
                                    )}
                                </Box>
                              }
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </>
                )}

                {/* No Results */}
                {searchText.length >= 3 && !loading && results.length === 0 && (
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      No results found for "{searchText}"
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Try searching with different keywords
                    </Typography>
                  </Box>
                )}

                {/* Search Tip */}
                {searchText.length > 0 && searchText.length < 3 && (
                  <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="caption" color="text.secondary">
                      Type at least 3 characters to search
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default SearchComponent;
